/**
 * MyMoneyOnAccountPanel — a fee earner's own Money on Account at a glance.
 * BM-H116. Scoped to the calling user's own active matters only — never
 * firm-wide. For fee earners who don't already get FirmMoneyBarCard's
 * firm-wide + per-fee-earner breakdown (Directors/Accounts/Admin).
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../config/api";
import MoneyBar from "./MoneyBar";

function fmt(n) {
  if (n == null || isNaN(n)) return "£0";
  return "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function MyMoneyOnAccountPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userId = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id");
    axios.get(`${API}/money-on-account/my-summary`, {
      headers: { "X-Bonsai-User-Id": userId || "" },
    })
      .then(r => setData(r.data))
      .catch((err) => {
        // BM-H153 (finance audit P1-7): fee earner's own MoA panel was
        // silently console.error'ing on failure and rendering nothing,
        // so it looked as though they had no client money on any
        // matter when in reality the endpoint had 5xx'd. Surface it.
        console.error("My MoA summary failed:", err);
        if (window.errorMessage) {
          window.errorMessage(
            `Your money-on-account summary failed to load: ${err?.response?.data?.detail || err?.message || "unknown error"}`
          );
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse h-24 bg-navy-card rounded-lg" />;
  if (!data) return null;

  const { totals, active_matter_count, matters, matters_truncated } = data;

  // Hide entirely when there is nothing to show — mirrors FirmMoneyBarCard's
  // convention so fee earners with no MOA activity don't get dashboard clutter.
  const hasFinancials = totals.quote > 0 || totals.wip > 0 || totals.money_on_account > 0;
  if (!hasFinancials && active_matter_count === 0) return null;

  return (
    <div className="bg-navy-card border border-navy-border rounded-lg p-4 mb-6" data-testid="my-moa-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">
          Money on Account
          <span className="ml-2 text-xs text-slate-400 font-normal">
            {active_matter_count} active matter{active_matter_count === 1 ? "" : "s"}
          </span>
        </h3>
        <span className="text-xs text-slate-300 font-medium" data-testid="my-moa-total">
          {fmt(totals.money_on_account)}
        </span>
      </div>

      <MoneyBar
        quote={totals.quote}
        wip={totals.wip}
        moneyOnAccount={totals.money_on_account}
      />

      {matters.length > 0 && (
        <div className="mt-4 space-y-1.5 border-t border-navy-border pt-3">
          {matters.map(m => (
            <div key={m.matter_reference} className="flex items-center justify-between text-xs">
              <span className="text-slate-300">{m.matter_reference}</span>
              <span className="text-slate-400">{fmt(m.money_on_account)}</span>
            </div>
          ))}
          {matters_truncated > 0 && (
            <div className="text-xs text-slate-500 pt-1">+{matters_truncated} more</div>
          )}
        </div>
      )}
    </div>
  );
}
